import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	makeClassName,
	Row,
	RowProps,
	StyleProps,
	TypeOrFunction,
	useLocalStorage,
	valuesTypeFilter,
} from "@hex-insights/core";
import { NavLink, NavLinkProps } from "@hex-insights/router";
import styles from "./styles.module.css";

export type NavProps = Partial<Pick<RowProps, "horizontalSpacing">> &
	Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function Nav({ horizontalSpacing, className, style, children }: NavProps) {
	return (
		<Row justify="spaced-start" horizontalSpacing={horizontalSpacing} className={className} style={style}>
			{children}
		</Row>
	);
}

Nav.Link = function ({ className, activeClassName, ...props }: NavLinkProps) {
	return (
		<NavLink
			{...props}
			className={makeClassName(styles["nav__link"], className)}
			activeClassName={makeClassName(styles["nav__link--location-active"], activeClassName)}
		/>
	);
};

const storageKeyPrefix = "ui.buttonNav.";

export function useButtonNav<T extends string>(
	storageKey: string,
	getOptions: TypeOrFunction<ButtonNavOption<T>[]>,
	{ defaultValue, initialValue }: { defaultValue?: T; initialValue?: T } = {},
) {
	const [options] = React.useState(getOptions);

	const { getItem, setItem } = useLocalStorage();
	const fullStorageKey = storageKeyPrefix + storageKey;
	const [value, setValue] = React.useState(() => {
		if (initialValue) {
			return initialValue;
		}
		return getItem(
			fullStorageKey,
			valuesTypeFilter(
				options.map((e) => e.value),
				defaultValue ?? options[0].value,
			),
		);
	});
	React.useEffect(() => {
		setItem(fullStorageKey, value);
	}, [setItem, fullStorageKey, value]);

	return { value, setValue, options };
}

export type ButtonNavOption<T extends string> = { value: T; label: React.ReactNode };

export type ButtonNavProps<T extends string> = {
	value: T;
	setValue: (action: React.SetStateAction<T>) => void;
	options: ButtonNavOption<T>[];
} & Partial<Pick<RowProps, "horizontalSpacing">> &
	Partial<ClassNameProps & StyleProps>;

export function ButtonNav<T extends string>({
	value,
	setValue,
	options,
	horizontalSpacing,
	className,
	style,
}: ButtonNavProps<T>) {
	return (
		<Row justify="spaced-start" horizontalSpacing={horizontalSpacing} className={className} style={style}>
			{options.map((e) => (
				<Button
					key={e.value}
					className={makeClassName(
						styles["button-nav__button"],
						value === e.value ? styles["button-nav__button--location-active"] : "",
					)}
					onClick={() => setValue(e.value)}
				>
					{e.label}
				</Button>
			))}
		</Row>
	);
}
